import { AgGridReact } from "ag-grid-react";
import {
  ColDef,
  FilterChangedEvent,
  ICellRendererParams,
  ModelUpdatedEvent,
  RowSelectionOptions,
  themeQuartz,
} from "ag-grid-community";
import { useCallback, useMemo, useState } from "react";
import { BookingReportDateBasis, BookingStatus, Currency } from "@/gql/graphql";
import dayjs from "dayjs";
import { useGqlQuery } from "@/lib/GraphQLCodegen/fetcher";
import { useSearch } from "@tanstack/react-router";
import {
  ReportBookingListItem,
  reportBookingListQuery,
} from "@/app/Reports/Reports/Components/ReportBookings/reportBookingsQuery";
import { useTranslations } from "use-intl";
import { useGlobalSupplier } from "@/app/Suppliers/Utils/useGlobalSupplier";
import { formatCurrency } from "@/lib/Formatters/formatCurrency";
import { bookingStatusColorMap } from "@/app/Bookings/Utils/bookingStatusColorMap";

const imoovaTheme = themeQuartz.withParams({
  browserColorScheme: "light",
  fontFamily: {
    googleFont: "Inter",
  },
  fontSize: 12,
  headerFontSize: 14,
  spacing: 5,
});

const defaultColDef = {
  floatingFilter: true,
};

const sidebar = {
  toolPanels: ["columns", "filters"],
};

export function ReportBookingsTable({ ref }: { ref: any }) {
  const { startDate, endDate, dateBasis } = useSearch({
    from: "/$tenant/$orgId/supplier/{-$supplierId}/reports/bookings",
  });
  const { supplierId } = useGlobalSupplier();

  const t = useTranslations("booking");

  const { data, isLoading, isError } = useGqlQuery(reportBookingListQuery, {
    rangeStartDate: startDate,
    rangeEndDate: endDate,
    supplierId: supplierId ?? undefined,
    dateBasis: dateBasis as BookingReportDateBasis,
  });

  const { supplier } = useGlobalSupplier();

  const columns = useMemo(() => {
    return [
      {
        field: "supplier_name",
        headerName: "Supplier",
        filter: true,
        initialHide: !!supplier,
        enableRowGroup: true,
      },
      {
        field: "booking_reference",
        headerName: "Reference",
        filter: "agTextColumnFilter",
      },
      {
        headerName: supplier
          ? `${supplier.name} reference`
          : "Supplier reference",
        field: "booking_supplier_reference",
        enableRowGroup: true,
      },
      {
        field: "booking_status",
        headerName: "Status",
        enableRowGroup: true,
        filter: "agSetColumnFilter",
        cellRenderer: (params: ICellRendererParams) => {
          if (params.value === undefined) {
            return null;
          }

          const status = params.value as BookingStatus;
          const color = bookingStatusColorMap[status];

          // Map color names to Tailwind classes
          const colorClasses: Record<typeof color, string> = {
            amber: "",
            cyan: "",
            emerald: "",
            fuchsia: "",
            indigo: "",
            lime: "",
            orange: "",
            pink: "",
            rose: "",
            teal: "",
            violet: "",
            yellow: "bg-yellow-100 text-yellow-800 border-yellow-200",
            zinc: "bg-zinc-100 text-zinc-800 border-zinc-200",
            sky: "bg-sky-100 text-sky-800 border-sky-200",
            blue: "bg-blue-100 text-blue-800 border-blue-200",
            green: "bg-green-100 text-green-800 border-green-200",
            red: "bg-red-100 text-red-800 border-red-200",
            purple: "bg-purple-100 text-purple-800 border-purple-200",
          };

          // Create badge component
          const BadgeComponent = () => {
            const badgeClass = `inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${colorClasses[color]}`;

            return <div className={badgeClass}>{t(`status.${status}`)}</div>;
          };

          return <BadgeComponent />;
        },
        filterParams: {
          valueFormatter: (params: any) => {
            return t(`status.${params.value as BookingStatus}`);
          },
        },
      },
      {
        headerName: "Origin",
        field: "departure_office_name",
        filter: "agTextColumnFilter",
        enableRowGroup: true,
      },
      {
        headerName: "Destination",
        field: "delivery_office_name",
        filter: "agTextColumnFilter",
        enableRowGroup: true,
      },
      {
        headerName: "Vehicle",
        field: "vehicle_name",
        filter: "agTextColumnFilter",
        enableRowGroup: true,
      },
      {
        headerName: "Trip cost (customer paid)",
        field: "booking_customer_cost",
        aggFunc: "sum",
        valueFormatter: (params) => {
          const currency = params.data?.booking_currency ?? Currency.Aud;
          return formatCurrency(params.value, currency, {
            emptyValue: "",
          });
        },
      },
      {
        field: "booking_depart_at",
        headerName: "Depart At",
        valueGetter: (params) => {
          // Check if data exists to avoid errors
          if (!params.data?.booking_depart_at) return null;

          // Normalize the date by setting it to the start of day in UTC
          // This removes time component issues for filtering
          return dayjs.utc(params.data.booking_depart_at).toDate();
        },
        valueFormatter: (params) => {
          if (!params.value) return "";
          // Format for display - using the original value from the data, not the normalized one
          return dayjs(params.data?.booking_depart_at).format("LLL");
        },
        filter: "agDateColumnFilter",
        filterParams: {
          // Ensure the filter component also works in the same timezone context
          comparator: (filterLocalDateAtMidnight: Date, cellValue: any) => {
            const normalizedFilterDate = dayjs(
              filterLocalDateAtMidnight,
            ).startOf("day");
            // Cell date is already normalized in valueGetter
            const normalizedCellDate = dayjs(cellValue).startOf("day");

            if (normalizedCellDate.isBefore(normalizedFilterDate, "day"))
              return -1;
            if (normalizedCellDate.isAfter(normalizedFilterDate, "day"))
              return 1;
            return 0;
          },
        },
      },
      {
        headerName: "Delivery At",
        field: "booking_deliver_at",
        valueGetter: (params) => {
          // Check if data exists to avoid errors
          if (!params.data?.booking_deliver_at) return null;

          // Normalize the date by setting it to the start of day in UTC
          // This removes time component issues for filtering
          return dayjs.utc(params.data.booking_deliver_at).toDate();
        },
        valueFormatter: (params) => {
          if (!params.value) return "";
          // Format for display - using the original value from the data, not the normalized one
          return dayjs(params.data?.booking_deliver_at).format("LLL");
        },
        filter: "agDateColumnFilter",
        filterParams: {
          // Ensure the filter component also works in the same timezone context
          comparator: (filterLocalDateAtMidnight: Date, cellValue: any) => {
            const normalizedFilterDate = dayjs(
              filterLocalDateAtMidnight,
            ).startOf("day");
            // Cell date is already normalized in valueGetter
            const normalizedCellDate = dayjs(cellValue).startOf("day");

            if (normalizedCellDate.isBefore(normalizedFilterDate, "day"))
              return -1;
            if (normalizedCellDate.isAfter(normalizedFilterDate, "day"))
              return 1;
            return 0;
          },
        },
      },
      {
        headerName: "Relocation",
        field: "relocation_reference",
        filter: "agTextColumnFilter",
      },
      {
        headerName: "Relocation posted (UTC)",
        field: "relocation_created_at",
        initialHide: true,
        valueFormatter: (params) => {
          if (!params.value) return "";
          return dayjs.utc(params.value).format("LLL");
        },
      },
      {
        headerName: "Booking created (UTC)",
        field: "booking_created_at",
        initialHide: true,
        valueFormatter: (params) => {
          if (!params.value) return "";
          return dayjs.utc(params.value).format("LLL");
        },
      },
      {
        headerName: "Booking confirmed (UTC)",
        field: "booking_confirmed_at",
        initialHide: true,
        valueFormatter: (params) => {
          if (!params.value) return "";
          return dayjs.utc(params.value).format("LLL");
        },
      },
      {
        headerName: "Days to booking",
        field: "days_to_booking",
        filter: "agNumberColumnFilter",
        aggFunc: "avg",
      },
      {
        headerName: "Customer name",
        field: "booking_name",
        filter: "agTextColumnFilter",
      },
      {
        headerName: "Customer email",
        field: "booking_email",
        filter: "agTextColumnFilter",
      },
      {
        headerName: "Customer phone",
        field: "booking_phone",
        filter: "agTextColumnFilter",
      },
      {
        headerName: "Fuel amount",
        field: "fuel_value",
        aggFunc: "sum",
        valueFormatter: (params) => {
          // Access the booking_currency from the same row
          const currency = params.data?.booking_currency ?? Currency.Aud;
          // Call your formatCurrency function
          return formatCurrency(params.value, currency, {
            emptyValue: "",
          });
        },
      },
      {
        headerName: "Fuel description",
        field: "fuel_description",
        filter: "agTextColumnFilter",
      },
      {
        headerName: "Ferry",
        field: "ferry_description",
        filter: "agTextColumnFilter",
      },
      {
        headerName: "Other value",
        field: "other_value",
        aggFunc: "sum",
        valueFormatter: (params) => {
          // Access the booking_currency from the same row
          const currency = params.data?.booking_currency ?? Currency.Aud;
          // Call your formatCurrency function
          return formatCurrency(params.value, currency, {
            emptyValue: "",
          });
        },
      },
      {
        headerName: "Other description",
        field: "other_description",
        filter: "agTextColumnFilter",
      },
    ] satisfies ColDef<ReportBookingListItem>[];
  }, [supplier, t]);

  const rowSelection = useMemo<
    RowSelectionOptions | "single" | "multiple"
  >(() => {
    return {
      mode: "multiRow",
    };
  }, []);

  const [filteredRows, setFilteredRows] = useState<ReportBookingListItem[]>([]);

  const updateFilteredRows = useCallback(
    (event: FilterChangedEvent | ModelUpdatedEvent) => {
      const rows: ReportBookingListItem[] = [];
      event.api.forEachNodeAfterFilter((node) => {
        if (node.data) rows.push(node.data);
      });
      setFilteredRows(rows);
    },
    [],
  );

  const pinnedBottomRowData = useMemo(() => {
    if (filteredRows.length === 0) {
      return [];
    }

    const totalBookings = filteredRows.length;

    const totalFuelValue = filteredRows.reduce((sum, booking) => {
      return sum + (booking.fuel_value || 0);
    }, 0);

    const totalOtherValue = filteredRows.reduce((sum, booking) => {
      return sum + (booking.other_value || 0);
    }, 0);

    const totalCustomerCost = filteredRows.reduce((sum, booking) => {
      return sum + (booking.booking_customer_cost || 0);
    }, 0);

    const currencies = filteredRows
      .map((booking) => booking.booking_currency)
      .filter(Boolean) as Currency[];

    const mostCommonCurrency =
      currencies.length > 0
        ? currencies.reduce((prev, curr, _, arr) =>
            arr.filter((v) => v === prev).length >=
            arr.filter((v) => v === curr).length
              ? prev
              : curr,
          )
        : Currency.Aud;

    return [
      {
        booking_reference: `Total Bookings: ${totalBookings}`,
        fuel_value: totalFuelValue,
        other_value: totalOtherValue,
        booking_customer_cost: totalCustomerCost,
        booking_currency: mostCommonCurrency,
        _isSummaryRow: true,
      },
    ];
  }, [filteredRows]);

  const getRowStyle = useCallback((params: any) => {
    // Apply special styling to summary rows
    if (params.data?._isSummaryRow) {
      return {
        backgroundColor: "#f3f4f6",
      };
    }
    return undefined;
  }, []);

  if (isError) {
    return <div>An error occurred while retrieving report</div>;
  }

  return (
    <AgGridReact<ReportBookingListItem>
      className="grow"
      ref={ref}
      rowData={data?.reportBookings ?? []}
      loading={isLoading}
      rowSelection={rowSelection}
      theme={imoovaTheme}
      columnDefs={columns}
      defaultColDef={defaultColDef}
      enableCharts={true}
      cellSelection={true}
      sideBar={sidebar}
      pinnedBottomRowData={pinnedBottomRowData}
      onFilterChanged={updateFilteredRows}
      onModelUpdated={updateFilteredRows}
      getRowStyle={getRowStyle}
    />
  );
}
